import { useResource } from '@landsbankinn/l16n';
import {
  Box,
  Col,
  HideIcon24,
  IconButton,
  Input,
  InputContainer,
  InputValidationMessage,
  Label,
  SeeIcon24,
} from '@landsbankinn/ui-kit';
import { useEffect, useRef, useState } from 'react';
import { Loading } from 'src/components/Loading';
import { useScreenSize } from 'src/hooks/useScreenSize';
import {
  AllowedLoginType,
  useApplicationClientContext,
  useApplicationServerContext,
  type LoginServerState,
} from 'src/state';
import { loginRes } from '../../Login.res';
import { DebugUserSelector } from '../DebugUserSelector';
import { LoginButtons } from '../LoginButtons';
import { LoginForm } from '../LoginForm';
import { res } from './Password.res';
import { usePasswordSubmit } from './usePasswordSubmit';
import { usePasswordValidation } from './usePasswordValidation';

export function Password({
  loginButtonHidden,
}: {
  loginButtonHidden?: boolean;
}) {
  const { r } = useResource();
  const [serverState] = useApplicationServerContext() as [LoginServerState];
  const [, clientDispatch] = useApplicationClientContext();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { onUsernameChanged, onPasswordChanged, onXsrf, passwordState } =
    usePasswordValidation();

  const { isMobile } = useScreenSize();

  const submit = usePasswordSubmit({
    onSubmitting: (submitting: boolean) => {
      setIsSubmitting(submitting);
      clientDispatch({
        type: 'setIsSubmitting',
        payload: { isSubmitting: submitting },
      });
    },
    onError: (error: string) => {
      clientDispatch({
        type: 'setLoginErrorState',
        payload: { errorMessage: error },
      });
    },
    onXsrf: (xsrf: string) => {
      clientDispatch({
        type: 'setFormValues',
        payload: {
          fields: [{ input: 'xsrf', value: xsrf }],
          loginType: AllowedLoginType.Password,
        },
      });
      onXsrf(xsrf);
    },
  });

  const [showValidation, setShowValidation] = useState<boolean>(false);
  const usernameRef = useRef<HTMLInputElement>(null);
  const passwordRef = useRef<HTMLInputElement>(null);

  const [showPassword, setShowPassword] = useState(false);
  const [curPos, setPos] = useState(0);

  function handleOnPressEye() {
    const currentPos = passwordRef.current?.selectionEnd || 0;
    setPos(currentPos);
    passwordRef.current?.focus();
    setShowPassword(!showPassword);
  }

  useEffect(() => {
    if (passwordRef) {
      passwordRef.current?.setSelectionRange(curPos, curPos);
    }
  }, [showPassword, curPos, passwordRef]);

  return (
    <LoginForm
      form={serverState.forms.password}
      fieldValueProvider={(name) => {
        if (name === 'xsrf' && passwordState.xsrf) {
          return passwordState.xsrf;
        }
        return serverState.forms.password.fields[name].value;
      }}
      onSubmit={(e) => {
        if (
          passwordState.password.validationState !== 'valid' ||
          passwordState.username.validationState !== 'valid'
        ) {
          setShowValidation(true);
          return;
        }
        e.preventDefault();
        submit(
          passwordState.username.value,
          passwordState.password.value,
          passwordState.xsrf,
        );
      }}
    >
      {isSubmitting && (
        <Col
          minHeight={isMobile ? undefined : '580'}
          mainAxisAlignment={isMobile ? undefined : 'center'}
        >
          <Loading
            title={r(loginRes.loadingTitle)}
            description={r(loginRes.loadingDescription)}
          />
        </Col>
      )}
      {!isSubmitting && (
        <>
          <Box marginBottom={'36'}>
            <Col paddingTop="36">
              <InputContainer
                direction="col"
                width="100%"
                validationState={passwordState.username.validationState}
                variant="text input col with validation"
              >
                <Label>{r(res.username)}</Label>
                <Input
                  instrumentAs="enter_username"
                  inputMode="text"
                  id="username"
                  name={serverState.forms.password.fields.userName.name}
                  maxLength={
                    serverState.forms.password.fields.userName.maxLength
                  }
                  autoFocus={false}
                  autoComplete="off"
                  variant="default"
                  ref={usernameRef}
                  value={passwordState.username.value}
                  onChange={(v) => {
                    onUsernameChanged(v.target.value);
                  }}
                  disabled={isSubmitting}
                />
                {showValidation && (
                  <InputValidationMessage
                    invalidMessage={r(res.validationUsernameInvalid)}
                    unsetMessage={r(res.validationUsernameRequired)}
                  />
                )}
              </InputContainer>
              <DebugUserSelector
                inputref={usernameRef}
                debugValues={
                  serverState.forms.password.fields.userName.debugValues
                }
                onUserSelect={({ value }) => {
                  const pass = `Password.For.${value}`;
                  onUsernameChanged(value);
                  onPasswordChanged(pass);
                }}
              />
              <InputContainer
                direction="col"
                width="100%"
                validationState={passwordState.password.validationState}
                variant="text input col with validation"
              >
                <Label>{r(res.password)}</Label>
                <Input
                  instrumentAs="enter_password"
                  id="password"
                  name={serverState.forms.password.fields.password.name}
                  maxLength={
                    serverState.forms.password.fields.password.maxLength
                  }
                  type={showPassword ? 'text' : AllowedLoginType.Password}
                  autoFocus={false}
                  autoComplete="off"
                  variant="default"
                  ref={passwordRef}
                  value={passwordState.password.value}
                  onChange={(v) => {
                    onPasswordChanged(v.target.value);
                  }}
                  disabled={isSubmitting}
                  suffix={
                    <IconButton
                      aria-label="Toggle password visibility"
                      paddingHorizontal="10"
                      onClick={handleOnPressEye}
                      instrumentAs="toggle_password_visibility"
                    >
                      {showPassword ? <SeeIcon24 /> : <HideIcon24 />}
                    </IconButton>
                  }
                />
                {showValidation && (
                  <InputValidationMessage
                    invalidMessage={r(res.validationPasswordInvalid)}
                    unsetMessage={r(res.validationPasswordRequired)}
                  />
                )}
              </InputContainer>
            </Col>
          </Box>

          <LoginButtons
            showRejectButton={serverState.isConsentFlow}
            showLoginButton={!loginButtonHidden}
            isValid={
              passwordState.username.validationState === 'valid' &&
              passwordState.password.validationState === 'valid'
            }
            loginType={AllowedLoginType.Password}
          />
        </>
      )}
    </LoginForm>
  );
}
